import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { RootState, AppDispatch } from '@/store';
import { fetchTickets } from '@/store/Ticket';
import FilterSelect from '@/components/filters/FilterSelect';
import Search from '@/components/filters/Search';
import { formatLocalDate } from '@/utils/FormateLocalDate';
import StatusBadge from '@/components/StatusBadge';
import { useNavigate } from 'react-router-dom';

const statusData: string[] = ['All', 'pending', 'in_progress', 'resolved'];

export default function Ticket() {
  const navigate = useNavigate();

  const dispatch = useDispatch<AppDispatch>();
  const {
    list: tickets,
    pagination,
    status,
  } = useSelector((state: RootState) => state.tickets);

  const [page, setPage] = useState(1);
  const [ticketStatus, setTicketStatus] = useState('All');
  const limit = 10;
  const [searchTerm, setSearchTerm] = useState('');
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState('');

  const loadTickets = (pageNum: number, append = false) => {
    dispatch(
      fetchTickets({
        page: pageNum,
        limit,
        status: ticketStatus !== 'All' ? ticketStatus : undefined,
        search: debouncedSearchTerm || undefined,
      }),
    );
  };

  useEffect(() => {
    setPage(1);
    loadTickets(1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ticketStatus, debouncedSearchTerm]);

  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedSearchTerm(searchTerm.trim());
    }, 420);

    return () => clearTimeout(handler);
  }, [searchTerm]);

  const handleScroll = (e: React.UIEvent<HTMLDivElement>) => {
    const { scrollTop, clientHeight, scrollHeight } = e.currentTarget;
    if (scrollHeight - scrollTop <= clientHeight + 5 && status !== 'loading') {
      if (pagination?.totalPages && page < pagination.totalPages) {
        const nextPage = page + 1;
        setPage(nextPage);
        loadTickets(nextPage, true);
      }
    }
  };

  const statusClasses: Record<string, string> = {
    pending: 'bg-yellow-400 text-black',
    'On Going': 'bg-cyan-400 text-black',
    Resolved: 'bg-green-700 text-white',
  };

  return (
    <div
      className='p-8 bg-white rounded-[24px] w-full h-full overflow-y-auto space-y-[24px]'
      onScroll={handleScroll}
    >
      {/* Top Bar */}
      <div className='flex flex-wrap gap-2 items-center'>
        <div className='font-semibold text-lg'>
          Showing {pagination?.totalItems || 0} Results
        </div>
        <div className='ml-auto flex flex-wrap gap-2 items-center'>
          <Search
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            placeholder='Search Issue and Description'
          />
          <FilterSelect
            value={ticketStatus}
            onChange={(e) => setTicketStatus(e.target.value)}
            options={[
              { value: 'All', label: 'Status: All' },
              ...statusData
                .filter((c) => c !== 'All')
                .map((c) => ({ value: c, label: `Status: ${c}` })),
            ]}
          />
        </div>
      </div>

      {/* Table */}
<table className="w-full table-fixed border-collapse">
  <thead className="sticky top-0 bg-white border-b">
    <tr>
      <th className="p-3 text-left w-[10%]">Date</th>
      <th className="p-3 text-left w-[32%]">ID</th>
      <th className="p-3 text-left w-[10%]">Issue</th>
      <th className="p-3 text-left w-[26%]">Description</th>
      <th className="p-3 text-left w-[12%]">Status</th>
      <th className="p-3 text-right w-[10%]">Action</th>
    </tr>
  </thead>
  <tbody>
    {tickets?.map((t) => (
      <tr key={t.id} className="border-b">
        <td className="p-3 text-left">{formatLocalDate(t.created_at)}</td>
        <td className="p-3 text-left">{t.id}</td>
        <td className="p-3 text-left">{t.issue_tag_name}</td>
        <td className="p-3 text-left truncate">{t.description}</td>
        <td className="p-3 text-left">
          <StatusBadge status={t.status} />
        </td>
        <td className="p-3 text-right pr-6">
          <div className="inline-flex gap-3">
            <button title="View" onClick={() => navigate(`/admin/show-ticket/${t.id}`)}>
              <svg
                xmlns="http://www.w3.org/2000/svg"
                className="w-5 h-5 text-gray-600"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"
                />
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M2.458 12C3.732 7.943 7.523 5 12 5c4.477 0 8.268 2.943 9.542 7-1.274 4.057-5.065 7-9.542 7-4.477 0-8.268-2.943-9.542-7z"
                />
              </svg>
            </button>
          </div>
        </td>
      </tr>
    ))}
    {status === "loading" && (
      <tr>
        <td colSpan={5} className="p-3 text-center text-sm text-gray-500">
          Loading...
        </td>
      </tr>
    )}
  </tbody>
</table>


    </div>
  );
}
